import { Add } from '@mui/icons-material'
import { Box, Button, Card, Stack, Typography } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetPaymentMethodWithCards,
  useUpdatePaymentMethodWithCards,
} from '@/services/hooks/relay/dynamic'
import { LiveListEntity } from '@/types/live-lists'
import { BasePaymentMethod } from '@/types/relay'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import AddCardNumberDialog from './AddCardNumberDialog'
import CardNumber from './CardNumber'
import { useCredentialTypeContext } from './CredentialTypeProvider'

type PaymentMethodWithCardNumbersProps = {
  orgId: string
  paymentMethod: LiveListEntity<BasePaymentMethod>
}

export default function PaymentMethodWithCardNumbers({
  orgId,
  paymentMethod,
}: PaymentMethodWithCardNumbersProps) {
  const overlay = useOverlay()
  const credentialType = useCredentialTypeContext()

  const { paymentMethodWithCards, queryKey } = useGetPaymentMethodWithCards({
    orgId,
    paymentMethodId: paymentMethod.entity_id,
    orgCredentialTypeId: credentialType.id,
  })

  const { updatePaymentMethodWithCards } = useUpdatePaymentMethodWithCards({
    orgCredentialTypeId: credentialType.id,
    onSuccess: () => {
      showSuccessSnackbar('Payment Method Updated')
    },
    onError: () => {
      showErrorSnackbar('Failed to add card number. Please try again later.')
    },
    detailQueryKey: queryKey,
  })

  function handleDeleteCardNumber(cardNumber: string) {
    if (!paymentMethodWithCards) return
    updatePaymentMethodWithCards({
      ...paymentMethodWithCards,
      card_numbers: paymentMethodWithCards.card_numbers.filter(
        (num) => num !== cardNumber,
      ),
    })
  }

  return paymentMethodWithCards ? (
    <Card
      sx={{ p: 1.5, borderRadius: 2.5, height: '100%', minHeight: 120 }}
      variant="outlined"
    >
      <Stack sx={{ height: '100%' }}>
        <Box>
          <Box>
            <Typography variant="subtitle1">
              {paymentMethod.pd_display_name}
            </Typography>
            {paymentMethodWithCards?.card_numbers.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                <i>None</i>
              </Typography>
            )}
          </Box>

          {paymentMethodWithCards?.card_numbers.map((cardNumber) => (
            <CardNumber
              key={cardNumber}
              cardNumber={cardNumber}
              onDelete={handleDeleteCardNumber}
            />
          ))}
        </Box>

        <Box flexGrow="1" />

        <Box>
          <Button
            size="small"
            variant="text"
            sx={{ fontSize: 12, ml: -0.6 }}
            startIcon={<Add sx={{ fontSize: 8, mt: -0.5, mr: -0.5 }} />}
            onClick={overlay.open}
          >
            Add Card
          </Button>
        </Box>
      </Stack>

      <AddCardNumberDialog
        overlay={overlay}
        existingCardNumbers={paymentMethodWithCards.card_numbers}
        onSave={(lastFour) => {
          updatePaymentMethodWithCards({
            ...paymentMethodWithCards,
            card_numbers: [
              ...paymentMethodWithCards.card_numbers,
              `${lastFour}`, // Convert to string
            ],
          })
        }}
      />
    </Card>
  ) : null
}
